import type { Pathway } from "../types";

import { links } from "../../linksRegistry";

export const otitisMediaPathway: Pathway = {
  id: "otitismedia",
  name: "Acute otitis media",
  population:
    "Children and young people aged 1 to 17 years only. Exclude: recurrent AOM (3+ in 6 months or 4+ in 12 months), pregnant individuals under 16.",
  pgdRefs: [
    "pgd-7a-phenazlido-aom",
    "pgd-7b-amox-aom",
    "pgd-7c-clari-aom",
    "pgd-7d-eryth-aom",
  ],
  links,
  clinicalNotes: [
    "Acute otitis media mainly affects children, can last for around 1 week, and over 80% of children recover spontaneously without antibiotics within 2–3 days from presentation.",
    "Offer self-care and regular pain relief to ALL patients regardless of whether antibiotics or ear drops are indicated.",
    "Antibiotics are only indicated for: (1) otorrhoea (discharge after eardrum perforation), or (2) children under 2 with bilateral AOM and symptoms >3 days or severe symptoms.",
    "Ear drops (phenazone/lidocaine) are for symptom relief in moderate/severe cases WITHOUT perforation — mutually exclusive with antibiotic eligibility.",
    "Otoscopic examination is required to confirm diagnosis and assess tympanic membrane status.",
  ],
  clinicalNoteLinkRefs: ["nice-cks-otitis-media", "healthier-together-earache", "nhs-england-pharmacy-first-clinical-pathways", "pgd-otitis-media-otigo", "pgd-otitis-media-amoxicillin", "pgd-otitis-media-clarithromycin", "pgd-otitis-media-erythromycin"],
  demographicConfiguration: {
    bannerFields: [
      { field: "dateOfBirth", label: "Date of birth", type: "date" },
      { field: "knownAllergies", label: "Known allergies", type: "string" },
    ],
  },
  demographicEligibility: {
    minAge: 1,
    maxAge: 18,
    blockMessage: "Acute otitis media (Pharmacy First) is only available for children and young people aged 1 to 17 years.",
  },
  exclusionPrefillMap: {},
  entryNode: "q1_emergency",
  nodes: [
    {
      id: "q1_emergency",
      type: "checklist",
      text: "Any suspected acute complications or emergency presentations?",
      showSepsisWarning: true,
      items: [
        { id: "meningitis", text: "Meningitis (neck stiffness, photophobia, mottled skin)" },
        { id: "mastoiditis", text: "Mastoiditis (pain, soreness, swelling, tenderness behind the affected ear)" },
        { id: "brain-abscess", text: "Brain abscess (severe headache, confusion or irritability, muscle weakness)" },
        { id: "sinus-thrombosis", text: "Sinus thrombosis (headache behind or around the eyes)" },
        { id: "facial-nerve-paralysis", text: "Facial nerve paralysis (drooping of the face)" },
        { id: "sepsis", text: "Signs of severe/life-threatening infection or systemic sepsis" },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_EMERGENCY",
      noneSelected: "q2_clinical_features",
    },
    {
      id: "GATEWAY_EMERGENCY",
      type: "gateway_alarm",
      heading: "EMERGENCY — A&E referral",
      body: "Signs or symptoms of serious complication suspected. Signpost to A&E or call 999.",
      action: "close",
      triggersPayment: true,
      leafletRefs: ["leaflet-otitis-media-en"],
    },
    {
      id: "q2_clinical_features",
      type: "yes_no",
      text: "Does the patient have acute onset of AOM symptoms AND tympanic membrane findings?",
      hint: "Symptoms: earache (older children), or holding/tugging ear, fever, crying, poor feeding, restlessness (younger children). TM findings on otoscopy: distinctly red/yellow/cloudy TM, moderate-severe bulging, OR perforation/sticky discharge.",
      required: true,
      clinicalLinks: [
        { label: "NICE CKS — Otitis media (acute)", url: links["nice-cks-otitis-media"].url, context: "reference" },
      ],
      yes: "GATEWAY_AOM_LIKELY",
      no: { outcomeId: "OUT_ALTERNATIVE_DIAGNOSIS" },
    },
    {
      id: "GATEWAY_AOM_LIKELY",
      type: "gateway_progression",
      heading: "AOM more likely",
      body: "Clinical features consistent with acute otitis media.",
      triggersPayment: true,
      next: "q3_serious_illness",
    },
    {
      id: "q3_serious_illness",
      type: "checklist",
      text: "Does the patient meet ANY of the following criteria requiring urgent referral?",
      items: [
        { id: "systemically-very-unwell", text: "Systemically very unwell" },
        { id: "serious-illness", text: "Signs of a more serious illness" },
        { id: "severely-immunosuppressed", text: "Severely immunosuppressed" },
        { id: "high-risk-comorbidity", text: "High risk of complications due to pre-existing comorbidity (significant heart, lung, renal, liver or neuromuscular disease, cystic fibrosis, or born prematurely)" },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_REFER_SERIOUS" },
      noneSelected: "q4_age",
    },
    {
      id: "q4_age",
      type: "yes_no",
      text: "Is the patient aged 1 year or over and under 18?",
      required: true,
      autoAnswer: { when: { patientAgeAtLeast: 1, patientAgeUnder: 18 }, value: true },
      yes: "q5_pregnant_check",
      no: { outcomeId: "OUT_CANNOT_AGE" },
    },
    {
      id: "q5_pregnant_check",
      type: "yes_no",
      text: "Is the patient currently pregnant or suspected pregnant?",
      required: true,
      storesTo: "isPregnant",
      autoAnswer: { when: { patientIsFemale: false }, value: false },
      yes: "q5_pregnant_age_gate",
      no: "q6_recurrent",
    },
    {
      id: "q5_pregnant_age_gate",
      type: "condition_route",
      conditions: [
        { when: { patientAgeUnder: 16 }, then: { outcomeId: "OUT_CANNOT_PREGNANT_UNDER16" } },
      ],
      default: "q6_recurrent",
    },
    {
      id: "q6_recurrent",
      type: "yes_no",
      text: "Is this recurrent AOM?",
      hint: "3 or more documented episodes in preceding 6 months, or 4 or more episodes in preceding 12 months with at least one in the past 6 months",
      required: true,
      yes: { outcomeId: "OUT_REFER_RECURRENT" },
      no: "q9_bilateral_under2",
    },
    {
      id: "q9_bilateral_under2",
      type: "yes_no",
      text: "Is the AOM in BOTH ears (bilateral)?",
      hint: "Asked for children under 2 years old. Bilateral AOM in this age group is a key indication for antibiotics.",
      required: true,
      autoAnswer: { when: { patientAgeAtLeast: 2 }, value: false },
      yes: "q10_bilateral_severity",
      no: "q8_otorrhoea",
    },
    {
      id: "q8_otorrhoea",
      type: "checklist",
      text: "Does the patient have ANY of the following?",
      items: [
        { id: "otorrhoea", text: "Otorrhoea (discharge after eardrum perforation)" },
        { id: "perforation", text: "Suspected or confirmed eardrum perforation" },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_ABX_ELIGIBLE",
      noneSelected: "q11_severity_eardrop",
    },
    {
      id: "q10_bilateral_severity",
      type: "checklist",
      text: "Does the patient meet ANY of the following?",
      items: [
        { id: "symptoms-over-3-days", text: "Symptoms present for more than 3 days" },
        { id: "severe-symptoms", text: "Severe symptoms based on clinician global impression" },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_ABX_ELIGIBLE",
      noneSelected: "q11b_offer_eardrops",
    },
    {
      id: "GATEWAY_ABX_ELIGIBLE",
      type: "gateway_progression",
      heading: "Antibiotic treatment indicated",
      body: "Patient meets antibiotic eligibility criteria. Proceeding to allergy and drug selection.",
      next: "q12_allergy",
    },
    {
      id: "q11_severity_eardrop",
      type: "severity",
      text: "What is the overall severity of symptoms?",
      mild: { outcomeId: "OUT_SELF_CARE_ONLY" },
      moderate: "q11b_offer_eardrops",
      severe: "q11b_offer_eardrops",
    },
    {
      id: "q11b_offer_eardrops",
      type: "yes_no",
      text: "Consider offering phenazone/lidocaine ear drops for symptom relief?",
      hint: "Per NICE: consider offering for moderate/severe symptoms.",
      required: true,
      yes: "eardrop_checks",
      no: { outcomeId: "OUT_SELF_CARE_ONLY" },
    },
    {
      id: "eardrop_checks",
      type: "pgd_exclusion_check",
      pgdIdOverride: "pgd-7a-phenazlido-aom",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY" },
      allCleared: { outcomeId: "OUT_SUPPLY_EARDROPS" },
    },
    {
      id: "q12_allergy",
      type: "yes_no",
      text: "Does the patient have a known penicillin allergy? (Including allergy to flucloxacillin or severe hypersensitivity to any beta-lactam)",
      required: true,
      storesTo: "hasPenicillinAllergy",
      yes: "abx_drug_select",
      no: "abx_drug_select",
    },
    {
      id: "abx_drug_select",
      type: "drug_selection",
      variant: "auto",
      rules: [
        { when: { hasPenicillinAllergy: false }, pgdId: "pgd-7b-amox-aom" },
        { when: { hasPenicillinAllergy: true, isPregnant: false }, pgdId: "pgd-7c-clari-aom" },
        { when: { hasPenicillinAllergy: true, isPregnant: true }, minAge: 16, maxAge: 17, pgdId: "pgd-7d-eryth-aom" },
        { when: { hasPenicillinAllergy: true, isPregnant: true }, maxAge: 15, cannotSupplyOutcomeId: "OUT_CANNOT_NO_PGD" },
      ],
      next: "abx_weight_capture",
    },
    {
      id: "abx_weight_capture",
      type: "weight_capture",
      text: "Confirm patient weight (kg) for paediatric clarithromycin dosing",
      hint: "Only required for children aged 1–11 receiving clarithromycin under PGD 7c.",
      minKg: 0.5,
      maxKg: 250,
      next: "abx_pgd_exclusions",
    },
    {
      id: "abx_pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY" },
      allCleared: { outcomeId: "OUT_SUPPLY_ABX" },
    },
  ],
  outcomes: {
    OUT_SUPPLY_ABX: { id: "OUT_SUPPLY_ABX", type: "outcome", outcomeType: "supply", title: "Supply oral antibiotic", reason: "Engine reads selectedPgdId for drug, dose band, formulations. Self-care and pain relief advice for ALL patients.", safetyNetting: ["Symptoms should start to improve within 72 hours of starting antibiotics", "Seek medical advice if symptoms do not improve or worsen within this time", "If symptoms worsen rapidly or significantly, or the child becomes very unwell — call 999 or go to A&E", "If symptoms do not improve after completing the 5-day course — return to pharmacy or contact GP", "Paracetamol or ibuprofen (weight-appropriate dose) should be used regularly for pain alongside the antibiotic", "Decongestants and antihistamines do not help with AOM symptoms"], leafletRefs: ["leaflet-otitis-media-en"], linkRefs: ["target-rti-leaflet", "nhs-ear-infection", "healthier-together-earache"] },
    OUT_SUPPLY_EARDROPS: { id: "OUT_SUPPLY_EARDROPS", type: "outcome", outcomeType: "supply", title: "Supply phenazone/lidocaine ear drops", reason: "Moderate/severe symptoms, no perforation, does not meet antibiotic eligibility. Self-care and pain relief alongside drops.", safetyNetting: ["Use for up to 7 days", "STOP using drops immediately if any discharge appears from the ear — return to pharmacy", "Paracetamol or ibuprofen (weight-appropriate dose) should also be used regularly for pain", "Return to pharmacy if no improvement within 3–5 days for reassessment", "Seek urgent help if symptoms worsen significantly or child becomes very unwell"], leafletRefs: ["leaflet-otitis-media-en"], linkRefs: ["target-rti-leaflet", "nhs-ear-infection", "healthier-together-earache"] },
    OUT_SELF_CARE_ONLY: { id: "OUT_SELF_CARE_ONLY", type: "outcome", outcomeType: "self_care", title: "Self-care and pain relief", reason: "AOM more likely but patient does not meet criteria for antibiotics or ear drops. Most children recover within 3 days without treatment.", safetyNetting: ["Paracetamol or ibuprofen (weight-appropriate dose) for pain — use regularly", "Decongestants and antihistamines do not help", "Without treatment, symptoms should start to improve within 3 days", "Return to pharmacy if no improvement within 3–5 days for reassessment", "Seek urgent help if symptoms worsen significantly or child becomes very unwell"], leafletRefs: ["leaflet-otitis-media-en"], linkRefs: ["target-rti-leaflet", "nhs-ear-infection", "healthier-together-earache"] },
    OUT_ALTERNATIVE_DIAGNOSIS: { id: "OUT_ALTERNATIVE_DIAGNOSIS", type: "outcome", outcomeType: "refer_routine", title: "Consider alternative diagnosis", reason: "Clinical features not consistent with AOM. Consider alternative diagnosis and proceed appropriately.", safetyNetting: ["Consider alternative diagnosis and proceed appropriately"], leafletRefs: ["leaflet-otitis-media-en"], linkRefs: ["nice-cks-otitis-media"] },
    OUT_REFER_SERIOUS: { id: "OUT_REFER_SERIOUS", type: "outcome", outcomeType: "refer_urgent", title: "Refer urgently — serious illness or high-risk comorbidity", reason: "Patient is systemically very unwell, has signs of serious illness, or is at high risk of complications. Refer to GP or prescriber urgently.", safetyNetting: ["Refer to GP or prescriber urgently"], leafletRefs: ["leaflet-otitis-media-en"] },
    OUT_REFER_RECURRENT: { id: "OUT_REFER_RECURRENT", type: "outcome", outcomeType: "refer_routine", title: "Refer — recurrent AOM", reason: "Recurrent AOM excluded from this pathway. Refer to GP or other provider.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-otitis-media-en"] },
    OUT_CANNOT_SUPPLY: { id: "OUT_CANNOT_SUPPLY", type: "outcome", outcomeType: "cannot_supply", title: "Cannot supply — PGD exclusion met", reason: "Refer to prescriber if treatment appropriate but falls outside this PGD.", safetyNetting: ["Refer to prescriber if treatment appropriate but falls outside this PGD"], leafletRefs: ["leaflet-otitis-media-en"], linkRefs: ["target-rti-leaflet", "nhs-ear-infection"] },
    OUT_CANNOT_AGE: { id: "OUT_CANNOT_AGE", type: "outcome", outcomeType: "cannot_supply", title: "Cannot supply — outside age range", reason: "This pathway covers children aged 1–17 years only. Refer to GP or other provider.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-otitis-media-en"] },
    OUT_CANNOT_PREGNANT_UNDER16: { id: "OUT_CANNOT_PREGNANT_UNDER16", type: "outcome", outcomeType: "cannot_supply", title: "Cannot supply — pregnant and under 16", reason: "Refer to GP or other provider.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-otitis-media-en"] },
    OUT_CANNOT_NO_PGD: { id: "OUT_CANNOT_NO_PGD", type: "outcome", outcomeType: "cannot_supply", title: "No PGD available — pregnant, under 16, penicillin allergic", reason: "No PGD covers this combination. Refer to GP.", safetyNetting: ["Refer to GP"], leafletRefs: ["leaflet-otitis-media-en"] },
  },
};
